import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  X, Sparkles, Loader2, FileText, Download, UploadCloud, Copy, Check, CheckCircle2,
  Settings2, Languages, Crosshair, Target, FileDown, ShieldCheck, Database, RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';

const REPORT_STYLES = [
  { value: 'executive', label: 'Executive' },
  { value: 'business', label: 'Business' },
  { value: 'technical', label: 'Technical' },
  { value: 'risk', label: 'Risk' },
  { value: 'gap', label: 'Gap' },
  { value: 'comparative', label: 'Comparative' },
  { value: 'strategic', label: 'Strategic' },
  { value: 'academic', label: 'Academic' },
  { value: 'summary', label: 'Summary' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children, icon: Icon }) => (
  <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
    {Icon ? <Icon className="h-3 w-3 text-emerald-700" /> : null}
    {children}
  </span>
);

const fieldCls =
  'flex w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 transition-all';

const normalizeReviewKind = (value) => {
  const kind = String(value || '').trim().toLowerCase();
  return kind === 'aar' || kind === 'exercise' || kind === 'drill' || kind === 'scenario'
    ? 'aar'
    : 'pir';
};

const objectiveForReview = (reviewKind) => reviewKind === 'aar'
  ? "Evaluate this exercise's end-to-end performance and produce a full, decision-ready After-Action Review."
  : "Evaluate this real incident's end-to-end response and produce a full, decision-ready Post-Incident Review.";

const analysisContextPayload = (response) => {
  const candidates = [response?.data?.data, response?.data, response];
  return candidates.find((candidate) => candidate && typeof candidate === 'object' && (
    typeof candidate.material === 'string'
    || candidate.reviewKind
    || candidate.review_kind
  )) || null;
};

const canonicalReviewPayload = (response) => {
  const candidates = [
    response?.data?.data?.review,
    response?.data?.review,
    response?.review,
    response?.data?.data,
    response?.data,
    response,
  ];
  return candidates.find((candidate) => candidate && typeof candidate === 'object' && (
    typeof candidate.markdown === 'string'
    || typeof candidate.report === 'string'
    || typeof candidate.content === 'string'
  )) || null;
};

const combineContextAndSupplementalMaterial = (authoritativeMaterial, supplementalMaterial) => {
  const authoritative = String(authoritativeMaterial || '').trim();
  if (!authoritative) return '';
  const supplemental = String(supplementalMaterial || '').trim();
  if (!supplemental) return authoritative;
  return `${authoritative}\n\n# Current unsaved/local supplemental material\n${supplemental}`;
};

const ExtensiveAnalysisModal = ({
  onClose,
  defaultMaterial = '',
  defaultObjective = '',
  title = 'Extensive Analysis',
  initialReport = '',
  onReportChange,
  incidentId = '',
  reviewKind = 'pir',
  contextSupplementalMaterial = '',
}) => {
  const initialReviewKind = normalizeReviewKind(reviewKind);
  const [material, setMaterial] = useState(defaultMaterial);
  const [objective, setObjective] = useState(defaultObjective || objectiveForReview(initialReviewKind));
  const [reportStyle, setReportStyle] = useState('executive');
  const [language, setLanguage] = useState('English');
  const [focusAreas, setFocusAreas] = useState('');
  const [report, setReport] = useState(initialReport);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [exporting, setExporting] = useState('');
  const [copied, setCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lastFile, setLastFile] = useState('');
  const [materialSource, setMaterialSource] = useState(incidentId ? 'context' : 'upload');
  const [contextMaterial, setContextMaterial] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState('');
  const [contextLoadedAt, setContextLoadedAt] = useState(null);
  const [loadedContextIncidentId, setLoadedContextIncidentId] = useState('');
  const [effectiveReviewKind, setEffectiveReviewKind] = useState(initialReviewKind);
  const [canonicalStatus, setCanonicalStatus] = useState(incidentId ? 'loading' : 'local');
  const [canonicalSavedAt, setCanonicalSavedAt] = useState('');
  const fileRef = useRef(null);
  const contextRequestRef = useRef(0);
  const reviewRequestRef = useRef(0);
  const generationRequestRef = useRef(0);
  const callerInitialReportRef = useRef(initialReport);
  const callerDefaultMaterialRef = useRef(defaultMaterial);
  const callerDefaultObjectiveRef = useRef(defaultObjective);
  const mountedIncidentIdRef = useRef(incidentId);
  const isRTL = language === 'Arabic';
  const isAar = effectiveReviewKind === 'aar';
  const reviewName = isAar ? 'After-Action Review' : 'Post-Incident Review';
  const reviewShortName = isAar ? 'AAR' : 'PIR';
  const contextSourceLabel = isAar ? 'Exercise Data' : 'Incident Data';
  const currentContextMaterial = loadedContextIncidentId === incidentId ? contextMaterial : '';
  const selectedMaterial = materialSource === 'context'
    ? combineContextAndSupplementalMaterial(currentContextMaterial, contextSupplementalMaterial)
    : material;

  useEffect(() => {
    callerInitialReportRef.current = initialReport;
    callerDefaultMaterialRef.current = defaultMaterial;
    callerDefaultObjectiveRef.current = defaultObjective;
  }, [defaultMaterial, defaultObjective, initialReport]);

  const loadIncidentContext = useCallback(async () => {
    if (!incidentId) return '';
    const requestId = ++contextRequestRef.current;
    setContextLoading(true);
    setContextError('');
    try {
      const response = await CommanServices.getIncidentAnalysisContext(incidentId);
      const payload = analysisContextPayload(response);
      const loadedMaterial = typeof payload?.material === 'string' ? payload.material.trim() : '';
      if (!loadedMaterial) throw new Error('No analysis material was returned for this record.');
      if (requestId !== contextRequestRef.current) return '';

      const serverReviewKind = normalizeReviewKind(payload?.reviewKind || payload?.review_kind || initialReviewKind);
      setEffectiveReviewKind(serverReviewKind);
      setContextMaterial(loadedMaterial);
      setLoadedContextIncidentId(incidentId);
      setContextLoadedAt(new Date());
      setObjective((current) => {
        const callerDefault = String(callerDefaultObjectiveRef.current || '').trim();
        const untouched = !current.trim()
          || current === callerDefault
          || current === objectiveForReview(initialReviewKind);
        return untouched ? objectiveForReview(serverReviewKind) : current;
      });
      return loadedMaterial;
    } catch (error) {
      if (requestId !== contextRequestRef.current) return '';
      const message = error?.response?.data?.detail || error?.message || 'Could not load authoritative analysis data.';
      setContextError(message);
      return '';
    } finally {
      if (requestId === contextRequestRef.current) setContextLoading(false);
    }
  }, [incidentId, initialReviewKind]);

  useEffect(() => {
    if (!incidentId) return undefined;
    const incidentChanged = mountedIncidentIdRef.current !== incidentId;
    mountedIncidentIdRef.current = incidentId;
    if (incidentChanged) {
      setMaterial(callerDefaultMaterialRef.current);
      setLastFile('');
      setObjective(callerDefaultObjectiveRef.current || objectiveForReview(initialReviewKind));
    }
    setMaterialSource('context');
    setContextMaterial('');
    setLoadedContextIncidentId('');
    setContextLoadedAt(null);
    setContextError('');
    setEffectiveReviewKind(initialReviewKind);
    setLoading(false);
    loadIncidentContext();
    return () => {
      contextRequestRef.current += 1;
      generationRequestRef.current += 1;
    };
  }, [incidentId, initialReviewKind, loadIncidentContext]);

  useEffect(() => {
    if (!incidentId) {
      setCanonicalStatus('local');
      return undefined;
    }
    const requestId = ++reviewRequestRef.current;
    setCanonicalStatus('loading');
    setCanonicalSavedAt('');
    setReport(callerInitialReportRef.current || '');
    (async () => {
      try {
        const response = await CommanServices.getIncidentReview(incidentId);
        if (requestId !== reviewRequestRef.current) return;
        if (response?.status === false) throw new Error(response?.message || 'Could not load the canonical review.');
        const payload = canonicalReviewPayload(response);
        const savedMarkdown = String(payload?.markdown || payload?.report || payload?.content || '').trim();
        if (savedMarkdown) {
          setReport(savedMarkdown);
          const savedKind = normalizeReviewKind(payload?.reviewKind || payload?.review_kind || initialReviewKind);
          setEffectiveReviewKind(savedKind);
          const savedLanguage = payload?.language;
          if (savedLanguage === 'English' || savedLanguage === 'Arabic') setLanguage(savedLanguage);
          const savedStyle = payload?.reportStyle || payload?.report_style;
          if (REPORT_STYLES.some((style) => style.value === savedStyle)) setReportStyle(savedStyle);
          if (typeof payload?.focusAreas === 'string' || typeof payload?.focus_areas === 'string') {
            setFocusAreas(payload?.focusAreas || payload?.focus_areas || '');
          }
          const savedObjective = String(payload?.objective || '').trim();
          setObjective(savedObjective || objectiveForReview(savedKind));
          setCanonicalSavedAt(payload?.updatedAt || payload?.updated_at || payload?.savedAt || payload?.saved_at || '');
          setCanonicalStatus('saved');
        } else {
          setCanonicalStatus('empty');
        }
      } catch (_error) {
        if (requestId === reviewRequestRef.current) setCanonicalStatus('error');
      }
    })();
    return () => {
      reviewRequestRef.current += 1;
    };
  }, [incidentId, initialReviewKind]);

  const handleMaterialSourceChange = (source) => {
    setMaterialSource(source);
    if (source === 'context' && incidentId && !currentContextMaterial && !contextLoading) {
      loadIncidentContext();
    }
  };

  const handleGenerate = async () => {
    const requestId = ++generationRequestRef.current;
    reviewRequestRef.current += 1;
    const targetIncidentId = incidentId;
    const usesAuthoritativeIncidentData = materialSource === 'context' && !!targetIncidentId;
    const inputMaterial = selectedMaterial;
    if (!usesAuthoritativeIncidentData && !inputMaterial.trim()) {
      toast.error(materialSource === 'context'
        ? `Could not load ${contextSourceLabel.toLowerCase()}. Refresh it or choose File Upload / Raw Text.`
        : 'Please add material to analyze (paste or upload).');
      return;
    }
    if (!objective.trim()) { toast.error('Please state the objective / report type.'); return; }
    setLoading(true);
    setReport('');
    try {
      const res = usesAuthoritativeIncidentData
        ? await CommanServices.incidentAnalysisGenerate(targetIncidentId, {
          objective,
          report_style: reportStyle,
          language,
          focus_areas: focusAreas,
          supplemental_text: String(contextSupplementalMaterial || '').trim(),
        })
        : await CommanServices.analysisGenerate({
          input_text: inputMaterial,
          objective,
          report_style: reportStyle,
          language,
          focus_areas: focusAreas,
        });
      if (requestId !== generationRequestRef.current) return;
      const generatedPayload = res?.data?.data || res?.data || res;
      const md = generatedPayload?.markdown;
      if (res?.status !== false && md) {
        const generatedReviewKind = usesAuthoritativeIncidentData
          ? normalizeReviewKind(generatedPayload?.reviewKind || generatedPayload?.review_kind || effectiveReviewKind)
          : effectiveReviewKind;
        const generatedMaterialSource = usesAuthoritativeIncidentData
          ? (generatedPayload?.materialSource || generatedPayload?.material_source || 'incidentData')
          : materialSource;
        if (usesAuthoritativeIncidentData) setEffectiveReviewKind(generatedReviewKind);
        setReport(md);
        if (onReportChange) onReportChange(md, {
          objective,
          reportStyle,
          language,
          reviewKind: generatedReviewKind,
          materialSource: generatedMaterialSource,
          incidentId,
        });
        if (targetIncidentId) {
          setCanonicalStatus('saving');
          try {
            const saveResponse = await CommanServices.saveIncidentReview(targetIncidentId, {
              markdown: md,
              reviewKind: generatedReviewKind,
              reportStyle,
              language,
              objective,
              focusAreas,
              materialSource: generatedMaterialSource,
            });
            if (requestId !== generationRequestRef.current) return;
            if (saveResponse?.status === false) {
              throw new Error(saveResponse?.message || 'Canonical review save failed.');
            }
            const saved = canonicalReviewPayload(saveResponse) || {};
            const savedKind = normalizeReviewKind(saved?.reviewKind || saved?.review_kind || effectiveReviewKind);
            setEffectiveReviewKind(savedKind);
            setCanonicalSavedAt(saved?.updatedAt || saved?.updated_at || saved?.savedAt || saved?.saved_at || new Date().toISOString());
            setCanonicalStatus('saved');
            toast.success(`${savedKind === 'aar' ? 'AAR' : 'PIR'} generated and saved`);
          } catch (_saveError) {
            if (requestId !== generationRequestRef.current) return;
            setCanonicalStatus('error');
            toast.warning('Report generated, but the canonical review could not be saved');
          }
        } else {
          toast.success('Report generated');
        }
      } else {
        toast.error(res?.message || 'Could not generate report');
      }
    } catch (e) {
      toast.error('Could not generate report');
    } finally {
      if (requestId === generationRequestRef.current) setLoading(false);
    }
  };

  const extractFile = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${API_URL}/api/analysis/extract`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const j = await r.json();
      const text = j?.data?.text || j?.text;
      if (text) {
        setMaterial((prev) => (prev ? `${prev}\n\n${text}` : text));
        setLastFile(file.name);
        toast.success(`Extracted ${file.name}`);
      } else {
        toast.error('Could not extract text from file');
      }
    } catch (err) {
      toast.error('File extraction failed');
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleUpload = (e) => extractFile(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    extractFile(e.dataTransfer.files?.[0]);
  };

  const handleExport = async (format) => {
    if (!report.trim()) { toast.error('Generate a report first'); return; }
    setExporting(format);
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${API_URL}/api/analysis/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ markdown: report, format, title }),
      });
      if (!r.ok) { toast.error('Export failed'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ext = format === 'md' ? 'md' : format;
      a.href = url;
      a.download = `${title.replace(/[^A-Za-z0-9._-]+/g, '_')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const wordCount = selectedMaterial.trim() ? selectedMaterial.trim().split(/\s+/).length : 0;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3 sm:p-6"
      data-testid="extensive-analysis-modal"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="extensive-analysis-title"
        className="flex w-full max-w-7xl h-[92vh] flex-col overflow-hidden rounded-xl border border-slate-200 bg-[#FAFAFA] shadow-2xl animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — government-grade authority bar */}
        <div className="flex items-center justify-between border-b border-emerald-900/20 bg-[#064E3B] px-6 py-3.5">
          <div className="flex items-center gap-3 text-white">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-white/10 ring-1 ring-white/20">
              <Sparkles className="h-5 w-5 text-emerald-200" />
            </div>
            <div className="leading-tight">
              <h2 id="extensive-analysis-title" className="text-sm font-bold tracking-tight">Extensive Analysis</h2>
              <p className="text-[11px] font-medium tracking-wide text-emerald-200/80">Decision-Ready {reviewName} ({reviewShortName})</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 rounded-sm bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-100 ring-1 ring-white/15">
              <ShieldCheck className="h-3 w-3" /> AI Intelligence
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close extensive analysis"
              className="text-white/70 transition-colors hover:text-white"
              data-testid="extensive-analysis-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
          {/* LEFT — input panel */}
          <div className="flex h-full w-full flex-col border-b border-slate-200 bg-white md:w-[42%] md:border-b-0 md:border-r lg:w-[38%]">
            <div className="flex-1 space-y-7 overflow-y-auto p-6 md:p-7">
              {/* Material */}
              <div>
                <Label icon={FileText}>Material to analyze *</Label>
                <Tabs value={materialSource} onValueChange={handleMaterialSourceChange} className="w-full">
                  <TabsList className={`grid w-full ${incidentId ? 'grid-cols-3' : 'grid-cols-2'} rounded-sm bg-slate-100 p-1`}>
                    {incidentId && (
                      <TabsTrigger value="context" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="ea-tab-context">
                        {contextSourceLabel}
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="upload" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="ea-tab-upload">
                      File Upload
                    </TabsTrigger>
                    <TabsTrigger value="text" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="ea-tab-text">
                      Raw Text
                    </TabsTrigger>
                  </TabsList>

                  {incidentId && (
                    <TabsContent value="context" className="mt-3">
                      <div className="rounded-sm border border-emerald-900/15 bg-emerald-50/60 p-4" data-testid="ea-context-source">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-3">
                            <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-sm bg-emerald-900 text-white">
                              {contextLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-emerald-950">Authoritative {contextSourceLabel}</p>
                              <p className="mt-1 text-xs leading-relaxed text-emerald-900/70">
                                {contextLoading
                                  ? 'Collecting the complete operational record…'
                                  : `Loaded directly from the ${isAar ? 'exercise' : 'incident'} record, timeline, response activity, communications and review evidence.`}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={loadIncidentContext}
                            disabled={contextLoading}
                            className="h-8 flex-none rounded-sm border-emerald-900/20 bg-white px-2.5 text-xs text-emerald-900"
                            data-testid="ea-context-refresh"
                          >
                            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${contextLoading ? 'animate-spin' : ''}`} /> Refresh
                          </Button>
                        </div>
                        {contextError && (
                          <div className="mt-3 flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-2.5 text-xs text-red-700" role="alert" data-testid="ea-context-error">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            <span>{contextError}</span>
                          </div>
                        )}
                        {!contextLoading && !contextError && currentContextMaterial && (
                          <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-emerald-800" data-testid="ea-context-loaded">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Ready for analysis{contextLoadedAt ? ` · refreshed ${contextLoadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  )}

                  <TabsContent value="upload" className="mt-3">
                    <input
                      ref={fileRef}
                      type="file"
                      className="hidden"
                      onChange={handleUpload}
                      accept=".pdf,.docx,.md,.markdown,.txt,.csv,.json,.log,.rst,.yml,.yaml"
                      data-testid="ea-file-input"
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Upload material to analyze"
                      onClick={() => fileRef.current?.click()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          fileRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      data-testid="ea-upload-btn"
                      className={`group relative flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed p-7 transition-all duration-300 ${
                        dragOver ? 'border-emerald-600 bg-emerald-50' : 'border-slate-300 bg-slate-50 hover:border-emerald-600/40 hover:bg-emerald-50/50'
                      }`}
                    >
                      {extracting ? (
                        <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
                      ) : (
                        <UploadCloud className="h-8 w-8 text-slate-400 transition-colors group-hover:text-emerald-700" />
                      )}
                      <p className="mt-3 text-center text-sm text-slate-600">
                        {extracting ? 'Extracting text…' : <>Drop a file or <span className="font-semibold text-emerald-800">browse</span></>}
                      </p>
                      <p className="mt-1 text-center text-[11px] text-slate-400">PDF · DOCX · TXT · CSV · MD</p>
                    </div>
                    {lastFile && !extracting && (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-800" data-testid="ea-extracted-file">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Extracted <span className="font-medium">{lastFile}</span>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="text" className="mt-3">
                    <textarea
                      value={material}
                      onChange={(e) => setMaterial(e.target.value)}
                      placeholder="Paste sections, headings, statuses, counts, values… The analysis works from the structure of this material."
                      className={`${fieldCls} min-h-[150px] resize-y`}
                      rows={7}
                      data-testid="ea-material-input"
                    />
                  </TabsContent>
                </Tabs>
                {wordCount > 0 && (
                  <p className="mt-1.5 text-right text-[10px] font-medium uppercase tracking-wider text-slate-400" data-testid="ea-word-count">
                    {wordCount.toLocaleString()} words loaded
                  </p>
                )}
              </div>

              {/* Objective */}
              <div>
                <Label icon={Target}>Objective / what to analyze *</Label>
                <textarea
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder={isAar
                    ? "e.g. Evaluate this exercise's response performance and produce a full After-Action Review."
                    : "e.g. Evaluate this real incident's response and produce a full Post-Incident Review."}
                  className={`${fieldCls} min-h-[72px] resize-y`}
                  rows={2}
                  data-testid="ea-objective-input"
                />
              </div>

              {/* Report type + Language */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label icon={Settings2}>Report type</Label>
                  <Select value={reportStyle} onValueChange={setReportStyle}>
                    <SelectTrigger className="rounded-sm border-slate-200 focus:ring-emerald-900" data-testid="ea-style-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[1200]">
                      {REPORT_STYLES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label icon={Languages}>Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="rounded-sm border-slate-200 focus:ring-emerald-900" data-testid="ea-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[1200]">
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Arabic">Arabic (العربية)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Focus areas */}
              <div>
                <Label icon={Crosshair}>Focus areas / constraints (optional)</Label>
                <input
                  value={focusAreas}
                  onChange={(e) => setFocusAreas(e.target.value)}
                  placeholder="e.g. SLA compliance, communication gaps, evacuation timing"
                  className={fieldCls}
                  data-testid="ea-focus-input"
                />
              </div>
            </div>

            {/* Generate CTA — sticky footer */}
            <div className="border-t border-slate-200 bg-white p-5">
              <Button
                onClick={handleGenerate}
                disabled={loading || (incidentId && contextLoading)}
                className="group relative h-12 w-full overflow-hidden rounded-sm bg-[#064E3B] font-semibold tracking-wide text-white shadow-[0_4px_14px_0_rgba(6,78,59,0.39)] transition-all hover:bg-[#022C22] hover:shadow-[0_6px_20px_rgba(6,78,59,0.30)] disabled:cursor-wait disabled:opacity-90"
                data-testid="ea-generate-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating decision-ready {reviewShortName}…</span>
                ) : (
                  <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Generate Report</span>
                )}
                {!loading && <span className="ea-gleam" aria-hidden="true" />}
              </Button>
            </div>
          </div>

          {/* RIGHT — report document canvas */}
          <div className="relative flex h-full w-full flex-col bg-[#FAFAFA] md:w-[58%] lg:w-[62%]">
            {/* Toolbar */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                <FileText className="h-3.5 w-3.5 text-emerald-700" /> Generated {reviewShortName}
              </span>
              <div className="flex items-center gap-1.5">
                {incidentId && (
                  <span
                    className={`mr-1 hidden items-center gap-1 text-[10px] font-semibold sm:inline-flex ${canonicalStatus === 'error' ? 'text-amber-700' : 'text-slate-500'}`}
                    data-testid="ea-canonical-status"
                    title={canonicalSavedAt ? `Last saved ${new Date(canonicalSavedAt).toLocaleString()}` : undefined}
                  >
                    {canonicalStatus === 'loading' || canonicalStatus === 'saving'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : canonicalStatus === 'saved'
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                        : canonicalStatus === 'error'
                          ? <AlertCircle className="h-3 w-3" />
                          : <Database className="h-3 w-3" />}
                    {canonicalStatus === 'loading'
                      ? 'Loading saved review…'
                      : canonicalStatus === 'saving'
                        ? 'Saving…'
                        : canonicalStatus === 'saved'
                          ? 'Canonical review saved'
                          : canonicalStatus === 'error'
                            ? 'Canonical save unavailable'
                            : 'No saved review yet'}
                  </span>
                )}
                <Button type="button" variant="outline" size="sm" disabled={!report} onClick={handleCopy} className="h-8 rounded-sm border-slate-200 text-slate-700" data-testid="ea-copy-btn">
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-700" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={!report || exporting} onClick={() => handleExport('md')} className="h-8 rounded-sm border-slate-200 text-xs text-slate-700" data-testid="ea-export-md">MD</Button>
                <Button type="button" variant="outline" size="sm" disabled={!report || exporting} onClick={() => handleExport('pdf')} className="h-8 rounded-sm border-slate-200 text-xs text-slate-700" data-testid="ea-export-pdf">
                  {exporting === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="flex items-center gap-1"><Download className="h-3.5 w-3.5" />PDF</span>}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={!report || exporting} onClick={() => handleExport('docx')} className="h-8 rounded-sm border-slate-200 text-xs text-slate-700" data-testid="ea-export-docx">
                  {exporting === 'docx' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="flex items-center gap-1"><FileDown className="h-3.5 w-3.5" />DOCX</span>}
                </Button>
              </div>
            </div>

            {/* Scanning beam overlay while generating */}
            {loading && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm" aria-live="polite" data-testid="ea-generating-overlay">
                <div className="relative h-40 w-40 overflow-hidden rounded-sm border border-emerald-900/15 bg-slate-50">
                  <div className="absolute left-0 top-0 h-[2px] w-full bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{ animation: 'scan 2s ease-in-out infinite' }} />
                  <div className="flex h-full flex-col items-center justify-center gap-2 px-3">
                    <Sparkles className="h-7 w-7 text-emerald-700" />
                    <div className="space-y-1.5">
                      <div className="h-1.5 w-20 rounded-full bg-slate-200" />
                      <div className="h-1.5 w-16 rounded-full bg-slate-200" />
                      <div className="h-1.5 w-24 rounded-full bg-slate-200" />
                    </div>
                  </div>
                </div>
                <p className="mt-4 animate-pulse text-sm font-medium tracking-wide text-emerald-900">Analyzing material · Building 26-section {reviewShortName}…</p>
              </div>
            )}

            {/* Canvas */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-8" dir={isRTL ? 'rtl' : 'ltr'} data-testid="ea-report-pane">
              {!report && !loading && (
                <div className="flex h-full flex-col items-center justify-center text-center" dir="ltr">
                  <div className="flex h-16 w-16 items-center justify-center rounded-sm border border-slate-200 bg-white shadow-sm">
                    <FileText className="h-8 w-8 text-slate-300" />
                  </div>
                  <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-600">
                    Turn {isAar ? 'exercise evidence' : 'real incident evidence'} into a <span className="font-semibold text-emerald-800">decision-ready {reviewName}</span> — 26 structured sections covering root cause, response performance, gaps and prioritized recommendations, generated in seconds.
                  </p>
                  <p className="mt-2 text-xs text-slate-400">Choose a material source, review the objective, then click <b className="text-slate-600">Generate Report</b>.</p>
                </div>
              )}
              {report && (
                <div className={`mx-auto w-full max-w-[820px] rounded-sm border border-slate-200 bg-white p-6 shadow-sm sm:p-10 ${isRTL ? 'text-right' : ''}`}>
                  <div className="prose prose-sm md:prose-base prose-slate max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-2 prose-a:text-emerald-700 prose-strong:text-slate-900 prose-table:text-xs prose-th:bg-slate-100">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtensiveAnalysisModal;
